import { database } from '../config/backend';
import { ref, set as rtdbSet, push, update as rtdbUpdate, get as rtdbGet } from './phpDataAdapter';
import { VideoCallSession, VideoCallStatus } from '../types/video';
import dailyService from './dailyService';
import { createDailyRoom, deleteDailyRoom } from '../config/daily';

/**
 * Cria uma nova sessão de videochamada
 */
export async function createVideoCallSession(
  nutritionistId: string,
  patientId: string,
  consultationId?: string
): Promise<VideoCallSession> {
  try {
    const sessionRef = ref(database, 'videoSessions');
    const newSessionRef = push(sessionRef);
    const sessionId = newSessionRef.key || '';

    // Criar room no Daily.co via API PHP da Hostinger
    const roomData = await createDailyRoom(sessionId, nutritionistId, patientId);

    const session: VideoCallSession = {
      id: sessionId,
      consultationId,
      channelName: sessionId, // Usar sessionId como channelName
      dailyRoomUrl: roomData.roomUrl,
      dailyRoomName: roomData.roomName,
      nutritionistId,
      patientId,
      status: 'idle',
      createdAt: new Date(),
    };

    await rtdbSet(newSessionRef, {
      ...session,
      createdAt: session.createdAt.toISOString(),
    });

    return session;
  } catch (error) {
    console.error('Erro ao criar sessão de videochamada:', error);
    throw error;
  }
}

/**
 * Inicia uma videochamada
 */
export async function startVideoCall(
  sessionId: string,
  userId: string,
  userRole: 'nutritionist' | 'patient'
): Promise<void> {
  try {
    const sessionRef = ref(database, `videoSessions/${sessionId}`);
    const sessionSnap = await rtdbGet(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Sessão de videochamada não encontrada');
    }

    const session = sessionSnap.val() as VideoCallSession;

    // Verificar se o usuário tem permissão
    if (userRole === 'nutritionist' && session.nutritionistId !== userId) {
      throw new Error('Usuário não autorizado');
    }
    if (userRole === 'patient' && session.patientId !== userId) {
      throw new Error('Usuário não autorizado');
    }

    // Atualizar status da sessão
    await rtdbUpdate(sessionRef, {
      status: 'connecting',
      startedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Erro ao iniciar videochamada:', error);
    throw error;
  }
}

/**
 * Finaliza uma videochamada
 */
export async function endVideoCall(sessionId: string): Promise<void> {
  try {
    // Sair da chamada Daily.co
    await dailyService.leaveCall();

    // Atualizar status da sessão
    const sessionRef = ref(database, `videoSessions/${sessionId}`);
    const sessionSnap = await rtdbGet(sessionRef);

    if (sessionSnap.exists()) {
      const session = sessionSnap.val() as VideoCallSession;
      const startedAt = session.startedAt ? new Date(session.startedAt) : new Date();
      const endedAt = new Date();
      const duration = Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000);

      await rtdbUpdate(sessionRef, {
        status: 'disconnected',
        endedAt: endedAt.toISOString(),
        duration,
      });

      // Deletar room do Daily.co (opcional)
      if (session.dailyRoomName) {
        try {
          await deleteDailyRoom(session.dailyRoomName);
        } catch (error) {
          console.error('Erro ao deletar room Daily.co:', error);
        }
      }
    }
  } catch (error) {
    console.error('Erro ao finalizar videochamada:', error);
    throw error;
  }
}

/**
 * Busca uma sessão de videochamada
 */
export async function getVideoCallSession(sessionId: string): Promise<VideoCallSession | null> {
  try {
    const sessionRef = ref(database, `videoSessions/${sessionId}`);
    const sessionSnap = await rtdbGet(sessionRef);

    if (!sessionSnap.exists()) {
      return null;
    }

    const data = sessionSnap.val();
    return {
      ...data,
      createdAt: new Date(data.createdAt),
      startedAt: data.startedAt ? new Date(data.startedAt) : undefined,
      endedAt: data.endedAt ? new Date(data.endedAt) : undefined,
    } as VideoCallSession;
  } catch (error) {
    console.error('Erro ao buscar sessão de videochamada:', error);
    return null;
  }
}

/**
 * Busca sessões de videochamada de um usuário
 */
export async function getUserVideoCallSessions(userId: string): Promise<VideoCallSession[]> {
  try {
    const sessionsRef = ref(database, 'videoSessions');
    const sessionsSnap = await rtdbGet(sessionsRef);

    if (!sessionsSnap.exists()) {
      return [];
    }

    const sessions = sessionsSnap.val() as Record<string, any>;
    const result: VideoCallSession[] = [];

    for (const [id, data] of Object.entries(sessions)) {
      if (data.nutritionistId === userId || data.patientId === userId) {
        result.push({
          id,
          ...data,
          createdAt: new Date(data.createdAt),
          startedAt: data.startedAt ? new Date(data.startedAt) : undefined,
          endedAt: data.endedAt ? new Date(data.endedAt) : undefined,
        } as VideoCallSession);
      }
    }

    // Ordenar por data (mais recente primeiro)
    result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    return result;
  } catch (error) {
    console.error('Erro ao buscar sessões de videochamada:', error);
    return [];
  }
}

/**
 * Atualiza status de uma sessão
 */
export async function updateVideoCallStatus(
  sessionId: string,
  status: VideoCallStatus
): Promise<void> {
  try {
    const sessionRef = ref(database, `videoSessions/${sessionId}`);
    await rtdbUpdate(sessionRef, {
      status,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Erro ao atualizar status da videochamada:', error);
    throw error;
  }
}
